import { useSearchParams } from "react-router";

export interface ListFilterControls {
  /** Current value of a filter, or an empty string when it is not set. */
  value: (key: string) => string;
  page: number;
  setFilter: (key: string, value: string) => void;
  setPage: (page: number) => void;
}

/**
 * List filters kept in the query string rather than in component state.
 *
 * That is what makes a dashboard card able to link straight to "not
 * reporting", and what lets someone send a colleague the exact list they are
 * looking at. Every list screen reads its filters through this.
 */
export function useListFilters(): ListFilterControls {
  const [searchParams, setSearchParams] = useSearchParams();

  function value(key: string): string {
    return searchParams.get(key) ?? "";
  }

  function setFilter(key: string, next: string): void {
    const params = new URLSearchParams(searchParams);

    if (next) {
      params.set(key, next);
    } else {
      params.delete(key);
    }

    // Any filter change invalidates the page number: page 3 of the old
    // result set is rarely page 3 of the new one.
    params.delete("page");
    // `replace` so typing in a filter does not bury the previous screen under
    // one history entry per keystroke.
    setSearchParams(params, { replace: true });
  }

  function setPage(next: number): void {
    const params = new URLSearchParams(searchParams);

    params.set("page", String(next));
    setSearchParams(params);
  }

  // A hand-edited `?page=abc` should show page one, not an empty table
  // produced by an offset of NaN.
  const page = Math.max(Math.trunc(Number(searchParams.get("page"))) || 1, 1);

  return { value, page, setFilter, setPage };
}
